import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  Warning,
  XCircle,
  SpeakerHigh,
} from '@phosphor-icons/react';

export type AudioStatus =
  | 'initializing'
  | 'connected'
  | 'only-microphone'
  | 'only-tab'
  | 'no-audio';

interface AudioStatusBadgeProps {
  status: AudioStatus;
  shrinkDelay?: number;
}

const statusConfig = {
  initializing: {
    icon: SpeakerHigh,
    i18nKey: 'recorderControls.audioStatus.initializing',
    fallback: 'Initializing audio detection...',
    className: 'bg-muted text-muted-foreground border-border',
    iconWeight: 'fill' as const,
  },
  connected: {
    icon: CheckCircle,
    i18nKey: 'recorderControls.audioStatus.connected',
    fallback: 'Audio connected',
    className:
      'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30',
    iconWeight: 'fill' as const,
  },
  'only-microphone': {
    icon: Warning,
    i18nKey: 'recorderControls.audioStatus.onlyMicrophone',
    fallback: 'Only microphone audio detected',
    className:
      'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30',
    iconWeight: 'fill' as const,
  },
  'only-tab': {
    icon: Warning,
    i18nKey: 'recorderControls.audioStatus.onlyTab',
    fallback: 'Microphone not detected',
    className:
      'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30',
    iconWeight: 'fill' as const,
  },
  'no-audio': {
    icon: XCircle,
    i18nKey: 'recorderControls.audioStatus.noAudio',
    fallback: 'Unable to detect audio',
    className: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30',
    iconWeight: 'fill' as const,
  },
};

const AudioStatusBadge: React.FC<AudioStatusBadgeProps> = ({
  status,
  shrinkDelay = 5000,
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(false);

    // Don't auto-collapse error states — keep them visible
    if (status === 'no-audio') return undefined;

    const timer = setTimeout(() => {
      setIsCollapsed(true);
    }, shrinkDelay);

    return () => clearTimeout(timer);
  }, [status, shrinkDelay]);

  const config = statusConfig[status];
  const { icon: Icon, i18nKey, fallback, className, iconWeight } = config;
  const label = t(i18nKey, fallback);

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 border rounded-full transition-all duration-500 ease-in-out overflow-hidden whitespace-nowrap flex-shrink-0',
        isCollapsed ? 'px-1.5 py-0.5' : 'px-2.5 py-0.5',
        className,
      )}
      title={label}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0" weight={iconWeight} />
      <span
        className={cn(
          'text-xs font-medium transition-all duration-500 ease-in-out',
          isCollapsed ? 'max-w-0 opacity-0 ml-0' : 'max-w-[300px] opacity-100',
        )}
      >
        {label}
      </span>
    </div>
  );
};

export default AudioStatusBadge;
